<script lang="ts" setup>
import { computed, type ComputedRef } from 'vue';
import type { SimpleListBtnData, SimpleListImage } from '../SimpleList.types';

const props = withDefaults(
  defineProps<{
    darkMode?: boolean;
    tags?: string[];
    headline: string;
    description: string;
    id: string;
    image?: SimpleListImage;
    buttonData: SimpleListBtnData;
  }>(),
  {
    tags: () => [],
  },
);

defineEmits<{ (event: 'click', id: string): void }>();

const hasImage: ComputedRef<boolean> = computed(() => !!props.image?.source);
</script>

<template>
  <a
    :class="['owpi-simple-list-item', { 'owpi-simple-list-item--only': !hasImage }]"
    :href="buttonData.url"
    :title="buttonData.label"
    target="_self"
    :data-id="id"
    tabindex="0"
    @click="$emit('click', id)"
    @keypress.enter.space="$emit('click', id)"
  >
    <wb-card :variant="darkMode ? 'dark-grey' : 'white'">
      <wb-aspect-ratio v-if="image && hasImage" class="owpi-simple-list-item__media" ratio="1x1">
        <img :src="`${image.source}?imwidth=156`" :alt="image.description" :title="image.title" loading="lazy" />
      </wb-aspect-ratio>

      <section class="owpi-simple-list-item__content">
        <h3 class="wb-type-copy-strong">{{ headline }}</h3>
        <br />
        <p v-if="description" class="wb-type-copy" v-html="description"></p>
      </section>

      <div v-if="buttonData" class="owpi-simple-list-item__btn">
        <div
          class="wb-link wb-round-button wb-round-button--solid-level-0 wb-round-button--medium"
          :class="{ 'wb-round-button--theme-dark': darkMode }"
        >
          <wb-icon class="wb-round-button__icon" aria-hidden="true" name="bds/chevron-right/24" flip-rtl></wb-icon>
          {{ buttonData.label }}
        </div>
      </div>
    </wb-card>
  </a>
</template>

<style lang="scss">
$simpleListSpacing: 8px;
$simpleListTagsMinHeight: 30px;

.owpi-simple-list-item {
  cursor: pointer;

  & wb-card {
    border: none;

    padding-inline-end: var(--wb-spacing-xs);
    gap: var(--wb-spacing-xs);
    display: flex;
    align-items: center;
  }

  &--only {
    & wb-card {
      padding-inline-start: var(--wb-spacing-xs);
      height: var(--vf-list-item-size);
    }
  }

  &__media {
    width: var(--vf-list-item-size);
    height: var(--vf-list-item-size);
  }

  &__content {
    --vf-list-item-gap: var(--wb-spacing-3xs);

    flex: 1;
    display: -webkit-box;
    -webkit-line-clamp: var(--vf-list-item-clamp);
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;

    padding-top: var(--vf-list-item-gap);

    h3,
    p {
      display: inline;
    }

    h3 {
      position: relative;
      top: calc(0px - var(--vf-list-item-gap));
    }
    p {
      line-height: 1.5rem;
    }
  }

  @media (pointer: coarse), (max-width: 768px) {
    &__btn {
      display: none;
    }
  }
}
</style>
